// EcosystemMatchesPanel — Unified list of matched ecosystem profiles + directory collaborators
import { useState } from 'react'
import { Star, User, Globe, Loader2, Database } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import type { EcosystemMatch } from '../../types'
import { unifiedMatchProfiles } from '../../services/api'

interface EcosystemMatchesPanelProps {
  matches: EcosystemMatch[]
  documentId?: string
  onMatchesFound?: (matches: EcosystemMatch[]) => void
}

const MATCH_TYPE_COLORS: Record<string, string> = {
  direct: 'bg-green-100 text-green-700',
  embedding: 'bg-blue-100 text-blue-700',
  cross_domain: 'bg-purple-100 text-purple-700',
}

const SOURCE_BADGE: Record<string, { label: string; className: string }> = {
  ecosystem_profile: { label: 'Profile', className: 'bg-sky-100 text-sky-700' },
  collaborator_directory: { label: 'Directory', className: 'bg-teal-100 text-teal-700' },
}

export default function EcosystemMatchesPanel({ matches, documentId, onMatchesFound }: EcosystemMatchesPanelProps) {
  const navigate = useNavigate()
  const [finding, setFinding] = useState(false)
  // hasSearched / searchError let us distinguish three empty-state cases:
  // never searched, searched-with-zero-results, and search-failed. Without
  // this the user can't tell if the directory is empty or the button is broken.
  const [hasSearched, setHasSearched] = useState(false)
  const [searchError, setSearchError] = useState(false)

  const handleFindMatches = async () => {
    if (!documentId) return
    setFinding(true)
    setSearchError(false)
    try {
      const results = await unifiedMatchProfiles(documentId)
      onMatchesFound?.(results)
      setHasSearched(true)
    } catch {
      setSearchError(true)
    } finally {
      setFinding(false)
    }
  }

  if (matches.length === 0) {
    return (
      <div className="p-3 space-y-3">
        {searchError ? (
          <div className="text-xs text-red-600" data-testid="ecosystem-matches-error">
            Search failed — please try again.
          </div>
        ) : hasSearched ? (
          <div className="text-xs text-gray-500 italic" data-testid="ecosystem-matches-empty">
            Search complete — no specialists matched this document yet. Try the
            full Marketplace below, or invite specialists to your network.
          </div>
        ) : (
          <div className="text-xs text-gray-500 italic">
            No specialists matched yet. Search the ecosystem and collaborator directory for relevant experts.
          </div>
        )}
        {documentId && (
          <button
            onClick={handleFindMatches}
            disabled={finding}
            className="w-full flex items-center justify-center gap-1.5 text-xs font-medium text-sky-600 py-2 border border-sky-200 rounded-lg hover:bg-sky-50 transition-colors disabled:opacity-50"
          >
            {finding ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin" />
                Searching specialists...
              </>
            ) : searchError || hasSearched ? (
              'Search again'
            ) : (
              'Find Specialists'
            )}
          </button>
        )}
        <button
          onClick={() => navigate('/ecosystem')}
          className="w-full flex items-center justify-center gap-1.5 text-[11px] text-gray-500 hover:text-sky-600 transition-colors"
        >
          <Globe className="w-3 h-3" />
          View Marketplace
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <h4 className="text-xs font-semibold text-gray-700">Matched Specialists</h4>
      {matches.map((match) => {
        const sourceBadge = SOURCE_BADGE[match.source || 'ecosystem_profile']
        const isDirectory = match.source === 'collaborator_directory'
        return (
          <div
            key={match.profile_id || match.entity_id || match.name}
            className={`flex items-center gap-2 p-2 border rounded-lg hover:bg-gray-50 ${
              isDirectory ? 'border-l-2 border-l-teal-400' : ''
            }`}
          >
            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center shrink-0">
              {match.avatar_url ? (
                <img src={match.avatar_url} alt="" className="w-8 h-8 rounded-full" />
              ) : isDirectory ? (
                <Database className="w-4 h-4 text-teal-500" />
              ) : (
                <User className="w-4 h-4 text-gray-500" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-gray-900 truncate">{match.name}</div>
              <div className="flex items-center gap-1">
                <span className="text-[10px] text-gray-500">{match.role || match.entity_type}</span>
                <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${sourceBadge.className}`}>
                  {sourceBadge.label}
                </span>
              </div>
            </div>
            <div className="flex flex-col items-end gap-0.5">
              {match.match_type && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${MATCH_TYPE_COLORS[match.match_type] || MATCH_TYPE_COLORS.direct}`}>
                  {match.match_type === 'cross_domain' ? 'Cross-Domain' : match.match_type}
                </span>
              )}
              {match.rating > 0 && (
                <div className="flex items-center gap-0.5">
                  <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                  <span className="text-[10px] text-gray-600">{match.rating.toFixed(1)}</span>
                </div>
              )}
              <div className="flex items-center gap-1">
                {match.match_percentage > 0 && (
                  <span className="text-[10px] text-gray-500">{match.match_percentage}%</span>
                )}
                {/* BUG-130: a per-match "Rate this match" thumbs-up was rendered
                    here with no onClick — a dead control. There is no lightweight
                    single-click rating endpoint (submitRating requires a full
                    6-dimension review), so the honest fix is to remove it rather
                    than fake an action. Ratings are captured via the full
                    ReputationProfile / rating form flow. */}
              </div>
            </div>
          </div>
        )
      })}

      {/* View Marketplace link */}
      <button
        onClick={() => navigate('/ecosystem')}
        className="w-full flex items-center justify-center gap-1.5 text-[11px] text-gray-500 hover:text-sky-600 pt-2 border-t border-gray-100 transition-colors"
      >
        <Globe className="w-3 h-3" />
        View Marketplace
      </button>
    </div>
  )
}
